import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import {
  configurationService,
  BrandingSettings as BrandingSettingsType,
} from '../../services/configurationService';
import { useTheme } from '../../theme/ThemeProvider';

export const BrandingSettings: React.FC = () => {
  const { updateBrandingSettings } = useTheme();
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const faviconFileInputRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState<BrandingSettingsType>({
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    logoUrl: '/logo.svg',
    faviconUrl: '/favicon.svg',
    companyName: 'GovIQ',
    applicationTitle: 'Your City Emergency Communications',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const brandingSettings = await configurationService.getBrandingSettings();
      setSettings(brandingSettings);
      setError('');
    } catch (_err) {
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await configurationService.updateBrandingSettings(settings);

      // Update the theme immediately with the new settings
      updateBrandingSettings(settings);

      // Verify the settings were saved by fetching them back
      setTimeout(async () => {
        try {
          const savedSettings = await configurationService.getBrandingSettings();
          updateBrandingSettings(savedSettings);
        } catch (_error) {
          // Silent error handling for settings verification
        }
      }, 500);

      setSuccess('Branding settings updated successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (_err) {
      setError('Failed to update branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setError('');
    setSuccess('');
  };

  const handleColorChange = (field: 'primaryColor' | 'secondaryColor', color: string) => {
    setSettings((prev) => ({ ...prev, [field]: color }));
  };

  // Convert a picked image file to a base64 data URL and store it directly in the
  // config value — no blob storage or external URLs required.
  const handleImageUpload = (field: 'logoUrl' | 'faviconUrl') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limit to 512 KB — base64 inflates by ~33%, config value is nvarchar(max) so size is fine,
    // but keep it reasonable so the DB row and HTTP responses stay small.
    if (file.size > 512 * 1024) {
      setError('Image must be under 512 KB. Resize or compress it first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const dataUrl = evt.target?.result as string;
      setSettings((prev) => ({ ...prev, [field]: dataUrl }));
    };
    reader.readAsDataURL(file);

    // Reset so the same file can be re-picked after a change
    e.target.value = '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Branding & Appearance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize the appearance and branding of your GovIQ CallScore platform.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Company Information */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>

              <TextField
                label="Company Name"
                value={settings.companyName}
                onChange={(e) => setSettings((prev) => ({ ...prev, companyName: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
                helperText="This will appear in the navigation and page titles"
              />

              <TextField
                label="Application Title"
                value={settings.applicationTitle}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, applicationTitle: e.target.value }))
                }
                fullWidth
                sx={{ mb: 2 }}
                helperText="The main title displayed in the application"
              />

              <TextField
                label="Logo URL"
                value={settings.logoUrl.startsWith('data:') ? '(uploaded image)' : settings.logoUrl}
                onChange={(e) => setSettings((prev) => ({ ...prev, logoUrl: e.target.value }))}
                disabled={settings.logoUrl.startsWith('data:')}
                fullWidth
                sx={{ mb: 1 }}
                helperText="Paste a URL or upload an image file below (PNG/SVG, max 512 KB)"
                placeholder="/logo.svg or https://example.com/logo.png"
              />
              {/* Hidden file input — triggered by the button below */}
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/png,image/svg+xml,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageUpload('logoUrl')}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => logoFileInputRef.current?.click()}
                >
                  Upload Logo
                </Button>
                {settings.logoUrl.startsWith('data:') && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => setSettings((prev) => ({ ...prev, logoUrl: '/logo.svg' }))}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              <TextField
                label="Favicon URL"
                value={settings.faviconUrl.startsWith('data:') ? '(uploaded image)' : settings.faviconUrl}
                onChange={(e) => setSettings((prev) => ({ ...prev, faviconUrl: e.target.value }))}
                disabled={settings.faviconUrl.startsWith('data:')}
                fullWidth
                sx={{ mb: 1 }}
                helperText="Paste a URL or upload an image file below (ICO/PNG/SVG, max 512 KB)"
                placeholder="/favicon.svg or https://example.com/favicon.ico"
              />
              <input
                ref={faviconFileInputRef}
                type="file"
                accept="image/x-icon,image/png,image/svg+xml"
                style={{ display: 'none' }}
                onChange={handleImageUpload('faviconUrl')}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => faviconFileInputRef.current?.click()}
                >
                  Upload Favicon
                </Button>
                {settings.faviconUrl.startsWith('data:') && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => setSettings((prev) => ({ ...prev, faviconUrl: '/favicon.svg' }))}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Color Scheme */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Color Scheme
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: settings.primaryColor,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      cursor: 'pointer',
                    }}
                  />
                  <TextField
                    value={settings.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Used for buttons, links, and primary UI elements
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Secondary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: settings.secondaryColor,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      cursor: 'pointer',
                    }}
                  />
                  <TextField
                    value={settings.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Used for accents, highlights, and secondary elements
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                <strong>Color Format:</strong> Use hex colors (e.g., #1976d2) or CSS color names
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Preview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>

          <Paper sx={{ p: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {settings.logoUrl && (
                <Avatar
                  src={settings.logoUrl}
                  alt="Company Logo"
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: 'contain',
                    '& img': {
                      objectFit: 'contain',
                    },
                  }}
                  variant="square"
                />
              )}
              <Box>
                <Typography variant="h6" sx={{ color: settings.primaryColor }}>
                  {settings.companyName}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {settings.applicationTitle}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: settings.primaryColor }}
              >
                Primary Button
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: settings.secondaryColor,
                  color: settings.secondaryColor,
                }}
              >
                Secondary Button
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary">
              This is how your branding will appear in the application.
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Settings'}
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={saving}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};
